import { useState, useEffect, useCallback, useRef } from 'react';
import type { Brief, StudioConfig } from '../types';

interface UseBriefOptions {
  config: StudioConfig;
  initialBrief: Brief | null;
  pollInterval?: number;
}

interface UseBriefReturn {
  brief: Brief | null;
  briefVersion: number;
  isLoading: boolean;
  error: string | null;
  setBrief: (brief: Brief | null) => void;
  updateBrief: (updates: Partial<Brief>) => Promise<Brief | undefined>;
  refreshBrief: () => Promise<void>;
}

async function apiFetch(url: string, method = 'GET', body?: unknown) {
  const opts: RequestInit = {
    method,
    headers: { 'Content-Type': 'application/json' },
  };
  if (body) opts.body = JSON.stringify(body);
  const res = await fetch(url, opts);
  if (!res.ok) throw new Error(`API ${res.status}: ${res.statusText}`);
  return res.json();
}

export function useBrief({ config, initialBrief, pollInterval = 5000 }: UseBriefOptions): UseBriefReturn {
  const [brief, setBrief] = useState<Brief | null>(initialBrief);
  const [briefVersion, setBriefVersion] = useState(initialBrief?.version ?? 0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const briefIdRef = useRef(brief?.brief_id);
  const hasFetchedRef = useRef(false);

  briefIdRef.current = brief?.brief_id;

  const apiBase = config.session?.api_base || '';
  const briefEndpoint = config.api_endpoints?.brief;

  // Fetch brief on mount if no initialBrief but we have an endpoint
  useEffect(() => {
    if (initialBrief || !briefEndpoint || hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    setIsLoading(true);
    apiFetch(briefEndpoint)
      .then((data) => {
        setBrief(data);
        setBriefVersion(data.version ?? 0);
        briefIdRef.current = data.brief_id;
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : 'Failed to load brief');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [initialBrief, briefEndpoint]);

  const refreshBrief = useCallback(async () => {
    const id = briefIdRef.current;
    if (!id) return;
    try {
      const updated = await apiFetch(`${apiBase}/api/v1/generation/briefs/${id}`);
      setBrief(updated);
      setBriefVersion(updated.version ?? 0);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to refresh brief');
    }
  }, [apiBase]);

  const updateBrief = useCallback(async (updates: Partial<Brief>): Promise<Brief | undefined> => {
    const id = briefIdRef.current;
    if (!id) return;
    setIsLoading(true);
    try {
      const result = await apiFetch(
        `${apiBase}/api/v1/generation/briefs/${id}`,
        'PATCH',
        updates,
      );
      setBrief(result);
      setBriefVersion(result.version ?? briefVersion + 1);
      setError(null);
      return result;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update brief');
    } finally {
      setIsLoading(false);
    }
  }, [apiBase, briefVersion]);

  // Poll for external changes when document is visible
  useEffect(() => {
    if (!briefIdRef.current || pollInterval <= 0) return;

    const poll = async () => {
      if (document.hidden) return;
      try {
        const updated = await apiFetch(
          `${apiBase}/api/v1/generation/briefs/${briefIdRef.current}`,
        );
        if (updated.version !== undefined && updated.version !== briefVersion) {
          setBrief(updated);
          setBriefVersion(updated.version);
        }
      } catch {
        // Silent poll failure
      }
    };

    pollRef.current = setInterval(poll, pollInterval);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [apiBase, briefVersion, pollInterval]);

  return {
    brief,
    briefVersion,
    isLoading,
    error,
    setBrief,
    updateBrief,
    refreshBrief,
  };
}
